import React from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { FloatingDock } from '../ui/floating-dock';
import { useWebDesignedFloatingDock, type DockLayoutMode } from './useWebDesignedFloatingDock';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';

const DOCK_HINT_STORAGE_KEY = 'webdesigned_dock_click_hint_dismissed';
const CALENDAR_IM_HERE_KEY = 'webdesigned_calendar_dock_im_here';

/**
 * Aceternity Floating Dock — quick jumps: Stays, Activities, Bundles, Cart.
 * Fixed above content; uses Lucide icons (not Tabler).
 */
const WebDesignedFloatingDock: React.FC<{ forcedLayoutMode?: DockLayoutMode }> = ({
  forcedLayoutMode = 'auto',
}) => {
  const { t } = useLanguage();
  const { items, isCompactShell } = useWebDesignedFloatingDock(forcedLayoutMode);
  const cart = useWebDesignedCart();
  const [dockHintVisible, setDockHintVisible] = React.useState(false);
  const [calendarImHere, setCalendarImHere] = React.useState(false);
  const wasAssistantOpen = React.useRef(false);

  React.useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    try {
      if (sessionStorage.getItem(DOCK_HINT_STORAGE_KEY) === '1') return;
    } catch {
      return;
    }
    setDockHintVisible(true);
    const tId = window.setTimeout(() => {
      setDockHintVisible(false);
      try {
        sessionStorage.setItem(DOCK_HINT_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
    }, 12000);
    return () => window.clearTimeout(tId);
  }, []);

  // First time the calendar assistant is closed → spotlight the dock calendar tile.
  React.useEffect(() => {
    const open = Boolean(cart?.assistantOpen && !cart?.assistantMinimized);
    if (wasAssistantOpen.current && !open) {
      try {
        if (sessionStorage.getItem(CALENDAR_IM_HERE_KEY) !== '1') {
          setCalendarImHere(true);
        }
      } catch {
        setCalendarImHere(true);
      }
    }
    // Re-opening the assistant clears the guide.
    if (open && calendarImHere) {
      setCalendarImHere(false);
      try {
        sessionStorage.setItem(CALENDAR_IM_HERE_KEY, '1');
      } catch {
        /* ignore */
      }
    }
    wasAssistantOpen.current = open;
  }, [cart?.assistantOpen, cart?.assistantMinimized, calendarImHere]);

  const dismissDockHint = React.useCallback(() => {
    setDockHintVisible(false);
    try {
      sessionStorage.setItem(DOCK_HINT_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const dismissCalendarImHere = React.useCallback(() => {
    setCalendarImHere(false);
    try {
      sessionStorage.setItem(CALENDAR_IM_HERE_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-dismiss “I am here” after a short spotlight.
  React.useEffect(() => {
    if (!calendarImHere) return;
    const tId = window.setTimeout(() => dismissCalendarImHere(), 10000);
    return () => window.clearTimeout(tId);
  }, [calendarImHere, dismissCalendarImHere]);

  const calendarSpotlight = calendarImHere ? (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-[#2f9e44]">
          {t('shoppingCart.calendarDockImHere') || 'I am here'}
        </p>
        <p className="mt-0.5 text-[11px] leading-snug text-[#3d2817]/80">
          {t('shoppingCart.calendarDockImHereDesc')}
        </p>
      </div>
      <button
        type="button"
        onClick={dismissCalendarImHere}
        className="-m-1 shrink-0 rounded-full p-1 text-[#3d2817]/60 hover:bg-black/5"
        aria-label={t('common.close') || 'Close'}
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  ) : null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[120] flex justify-center"
      style={{
        bottom: isCompactShell
          ? 'calc(var(--safe-area-bottom,var(--mock-safe-area-bottom,env(safe-area-inset-bottom))) + 8px)'
          : '2rem',
      }}
      aria-label={t('common.quickNavigation') || 'Quick navigation'}
    >
      <div className="pointer-events-auto mx-auto flex w-max max-w-[min(100%,92vw)] flex-col items-center gap-2 px-2">
        {dockHintVisible && !calendarImHere ? (
          <div
            role="status"
            className="flex max-w-[min(92vw,22rem)] items-start gap-2 rounded-2xl border border-[#ece3de] bg-white/98 px-3 py-2.5 text-left text-[11px] leading-snug text-[#3d2817] shadow-lg sm:text-xs"
          >
            <span className="min-w-0 flex-1 pt-0.5 text-[#3d2817]/85">{t('common.dockClickHint')}</span>
            <button
              type="button"
              onClick={dismissDockHint}
              className="-m-1 shrink-0 rounded-full p-1 text-[#3d2817]/55 transition-colors hover:bg-black/5"
              aria-label={t('common.close') || 'Close'}
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        ) : null}
        <FloatingDock
          items={items}
          desktopClassName="border border-[#ece3de] bg-gray-50/95 shadow-lg backdrop-blur-md"
          mobileClassName=""
          itemClassName="bg-gray-200 text-[#3d2817] hover:bg-[#d9c6b5] [&_svg]:text-[#3d2817]"
          forceLight
          spotlightKey={calendarImHere ? 'calendar' : null}
          spotlightContent={calendarSpotlight}
        />
      </div>
    </div>
  );
};

export default WebDesignedFloatingDock;
